import { ampli } from '@/ampli';
import { useState } from 'react';

import { AvatarSkeleton, TextSkeleton } from '@/components/Skeleton';
import { TokenPriceChart } from '@/components/TokenPriceChart';
import { SwitchButton } from '@/components/TokenProfile/SwitchButton';
import { TokenProfileContractAddress } from '@/components/TokenProfile/TokenProfileContractAddress';
import { TokenSharingModal } from '@/components/TokenProfile/TokenSharingModal';

import { ADVANCED_ORDER_TYPE_LABELS } from '@/constants/advancedOrder';

import { useAdvancedOrderType } from '@/hooks/useAdvancedOrderType';
import { CheckoutModes, useCheckoutMode } from '@/hooks/useCheckoutMode';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';
import { ShareIcon } from '@/ui/Icons/ShareIcon';
import { SmallDotIcon } from '@/ui/Icons/SmallDotIcon';
import { Panel } from '@/ui/Panel';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import { classNames } from '@/utils/classnames';
import { isTokenResultNativeAsset } from '@/utils/multichain';
import { tokenKey } from '@/utils/normalizeAddress';

import { TokenPanelStats } from './TokenPanelStats';
import { useLimitPriceLine } from './useLimitPriceLine';

const CHART_HEIGHT = 338;

export type TokenPanelSide = 'sell' | 'buy';

type TokenPanelProps = {
  sellToken: TokenResult | undefined;
  buyToken: TokenResult | undefined;
  side: TokenPanelSide;
  onSideChange: (side: TokenPanelSide) => void;
  chartHeight?: number;
  /** The bottom sheet is opened for one side already, so it hides the switch. */
  showSwitchButton?: boolean;
  embedded?: boolean;
  active?: boolean;
  className?: string;
};

export const TokenPanel = ({
  sellToken,
  buyToken,
  side,
  onSideChange,
  chartHeight = CHART_HEIGHT,
  showSwitchButton = true,
  embedded = false,
  active = true,
  className,
}: TokenPanelProps) => {
  const token = side === 'buy' ? buyToken : sellToken;
  const otherToken = side === 'buy' ? sellToken : buyToken;
  const [checkoutMode] = useCheckoutMode();
  const [advancedOrderType] = useAdvancedOrderType();
  const [shareOpenTime, setShareOpenTime] = useState(0);
  const [isShareOpen, setShareOpen] = useState(false);
  const limitPriceUsd = useLimitPriceLine({
    sellToken,
    buyToken,
    side,
    enabled: active && checkoutMode === CheckoutModes.advanced,
  });

  const showTokenAddress = !!token && !isTokenResultNativeAsset(token);
  const Container = embedded ? 'div' : Panel;

  const handleShare = () => {
    if (!token) return;
    setShareOpenTime(Date.now());
    setShareOpen(true);
    ampli.tokenShareClicked({
      token_address: token.address,
      token_chain_id: token.chainId,
      token_chain_name: token.chainName,
      token_symbol: token.symbol,
    });
  };

  return (
    <Container className={classNames('flex flex-col gap-3 py-4', className)}>
      {/* The avatar sits beside the name/symbol column, with the switch button pushed to
          the far end of the row. */}
      <div className="flex min-w-0 items-center gap-3 px-3 tablet:px-6">
        {token ? (
          <TokenAvatar address={token.address} chainId={token.chainId} size={40} />
        ) : (
          <AvatarSkeleton size={40} />
        )}

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {token ? (
            <div className="flex min-w-0 items-center gap-1">
              <span
                className={fontStyles({
                  variant: 'H5',
                  class: 'min-w-0 truncate',
                })}
              >
                {token.name}
              </span>
              <button
                aria-label={`Share ${token.symbol}`}
                className={buttons({
                  color: 'transparent',
                  shape: 'circle',
                  size: 'extraSmall',
                  iconOnly: true,
                  class: 'size-6 shrink-0 p-0 text-token-gray-700',
                })}
                onClick={handleShare}
                type="button"
              >
                <ShareIcon className="size-3.5" />
              </button>
            </div>
          ) : (
            <TextSkeleton width={110} height={22} />
          )}

          {token ? (
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'flex min-w-0 items-center text-token-gray-700 leading-[20px]',
              })}
            >
              <span className="min-w-0 truncate">{token.symbol}</span>
              {/* Beside the symbol at every width: the stats row below is a
                  single scrollable line on phones, so this buys it space. */}
              {showTokenAddress && (
                <>
                  <SmallDotIcon className="ml-1 shrink-0 text-token-gray-500" />
                  {/* The button's own padding is trimmed onto the dot's rhythm. */}
                  <TokenProfileContractAddress token={token} className="-ml-1 shrink-0" />
                </>
              )}
            </div>
          ) : (
            <TextSkeleton width={80} height={20} />
          )}
        </div>

        {showSwitchButton && (
          <SwitchButton
            activeToken={token}
            inactiveToken={otherToken}
            onTokenSelect={() => onSideChange(side === 'buy' ? 'sell' : 'buy')}
            className="ml-auto shrink-0"
          />
        )}
      </div>

      {/* Always rendered: `min-h-6` matches the tallest badge so the chart below
          keeps its position while the token and its stats resolve. */}
      <div
        className={fontStyles({
          variant: 'BODY_MD',
          class: [
            // Below tablet the stats stay on a single scrollable row.
            'flex min-h-6 flex-row flex-nowrap tablet:flex-wrap items-center *:shrink-0',
            'overflow-x-auto no-scrollbar tablet:overflow-visible',
            'gap-1 tablet:gap-2 px-3 tablet:px-6',
            'text-token-gray-700 leading-[20px]',
          ],
        })}
      >
        {!token && <TextSkeleton width={180} height={16} />}
        {token && active && (
          <TokenPanelStats key={tokenKey(token.address, token.chainId)} token={token} />
        )}
      </div>

      {/* The chart's price summary is pulled 8px above its box, so the wrapper
          gives it back and the identity, stats and price rows keep one rhythm. */}
      <div className="mt-2">
        <TokenPriceChart
          token={token}
          metricSelectorPlacement="chartTopRight"
          height={chartHeight}
          showLabels
          showVolume
          showTimeframes
          showTokenPrice
          showMetricSelector
          enableCandlestickView
          autoRefresh
          showTradeMarkers
          priceLineUsd={limitPriceUsd}
          priceLineLabel={ADVANCED_ORDER_TYPE_LABELS[advancedOrderType]}
          active={active}
        />
      </div>

      {token && (
        <TokenSharingModal
          address={token.address}
          chainId={token.chainId}
          symbol={token.symbol}
          openTime={shareOpenTime}
          isOpen={isShareOpen}
          handleOpenChange={setShareOpen}
        />
      )}
    </Container>
  );
};
